// A single, global popup/overlay layer for the UI system.
//
// The problem it solves: elements like an expanded drop down list draw OVER
// other UI elements, but input and hover are dispatched in tree order - so a
// sibling that happens to lie underneath the popup consumes the events and
// the popup breaks (e.g. a drop down that opens upwards loses hover to the
// row above it and disappears).
//
// Instead of reordering the whole tree, the element that owns a popup claims
// the overlay while it is open. Screens that host UI then do three things:
//   1. give the overlay first chance at input:   if(UIOverlay.mousePressed()) return;
//   2. render it last, above everything else:    UIOverlay.render();
//   3. skip hover tooltips while the mouse is over it:
//                                                 if(!UIOverlay.containsMouse()){ ...renderTooltip()... }
//
// Only one overlay can be open at a time (opening a new one implicitly
// replaces the old one), which is also the expected UX for popups.

export interface OverlayOwner {
    //draw the popup content - called after everything else has rendered
    renderOverlay(): void;
    //handle a mouse press while the overlay is open. Return true when the
    //event was consumed (an overlay usually consumes every click: either
    //the click hits the popup, or it closes the popup and is swallowed)
    overlayMousePressed(): boolean;
    //true when the mouse is currently over the popup (used to suppress
    //hover effects/tooltips of the UI underneath)
    overlayContainsMouse(): boolean;
}

class UIOverlay {
    static active: OverlayOwner | null = null;

    static claim(owner: OverlayOwner){
        UIOverlay.active = owner;
    }

    //releases the overlay, but only if it is still owned by "owner"
    static release(owner: OverlayOwner){
        if(UIOverlay.active === owner){
            UIOverlay.active = null;
        }
    }

    //force-close whatever is open (tool/screen switches, UI rebuilds)
    static clear(){
        UIOverlay.active = null;
    }

    static isOwner(owner: OverlayOwner): boolean {
        return UIOverlay.active === owner;
    }

    static render(){
        UIOverlay.active?.renderOverlay();
    }

    static mousePressed(): boolean {
        if(!UIOverlay.active){
            return false;
        }
        return UIOverlay.active.overlayMousePressed();
    }

    static containsMouse(): boolean {
        if(!UIOverlay.active){
            return false;
        }
        return UIOverlay.active.overlayContainsMouse();
    }
}

export default UIOverlay;
